import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Crown, Sliders, Check, X, FileQuestion, RotateCw } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const TABS = [
  { id: "verifications", label: "Verifications", icon: ShieldCheck },
  { id: "early_adopters", label: "Early Adopters", icon: Crown },
  { id: "config", label: "Pricing & quotas", icon: Sliders },
];

export default function AdminBilling() {
  const { user } = useAuth();
  const [tab, setTab] = React.useState("verifications");

  if (!user?.is_admin) {
    return <Layout><div className="text-ink-700">Admin access required.</div></Layout>;
  }

  return (
    <Layout>
      <div data-testid="admin-billing-page" className="max-w-5xl">
        <div className="label-overline mb-2">Admin</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-ink-900 mb-6">Billing & verification</h1>
        <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-subnav">
          <Link to="/admin/usage" data-testid="admin-subnav-usage" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-white border border-paper-200 text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">AI Usage</Link>
          <Link to="/admin/production" data-testid="admin-subnav-production" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-white border border-paper-200 text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">Production</Link>
          <Link to="/admin/billing" data-testid="admin-subnav-billing" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-ink-900 text-white inline-flex items-center gap-2">Billing & Verification</Link>
          <Link to="/admin/feedback" data-testid="admin-subnav-feedback" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-white border border-paper-200 text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">Feedback</Link>
        </div>
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`min-h-[40px] px-4 rounded-full text-sm inline-flex items-center gap-2 transition ${active ? "bg-ink-900 text-white" : "bg-white border border-paper-200 text-ink-700 hover:bg-paper-100"}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
        {tab === "verifications" && <VerificationsTab />}
        {tab === "early_adopters" && <EarlyAdoptersTab />}
        {tab === "config" && <ConfigTab />}
      </div>
    </Layout>
  );
}

// ============================================================
function VerificationsTab() {
  const [items, setItems] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/billing/verifications", { params: statusFilter ? { status: statusFilter } : {} });
      setItems(r.data);
    } catch (e) {
      toast.error("Could not load verifications.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  React.useEffect(() => { load(); }, [load]);

  const decide = async (userId, decision) => {
    const notes = decision === "approve" ? "" : (window.prompt(decision === "deny" ? "Reason for denial?" : "What documents are missing?") || "");
    try {
      await api.post(`/admin/billing/verifications/${userId}/decision`, { decision, notes });
      toast.success(`Marked ${decision}.`);
      load();
    } catch (e) {
      toast.error("Decision failed.");
    }
  };

  return (
    <div data-testid="verifications-tab">
      <div className="flex items-center gap-2 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="verifications-status-filter"
          className="min-h-[40px] px-3 rounded-full border border-paper-200 bg-white text-sm"
        >
          <option value="">Pending only</option>
          <option value="pending">Pending</option>
          <option value="needs_documents">Needs documents</option>
          <option value="verified">Verified</option>
          <option value="denied">Denied</option>
        </select>
        <button onClick={load} className="min-h-[40px] px-3 rounded-full border border-paper-200 text-sm hover:bg-paper-100 inline-flex items-center gap-1.5">
          <RotateCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      {loading ? (
        <div className="text-ink-500 animate-pulse">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-ink-500 text-sm">No verifications to show.</div>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li key={it.user_id} data-testid={`verification-row-${it.user_id}`} className="rounded-3xl border border-paper-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-ink-900">{it.name} · <span className="text-ink-600 font-normal">{it.email}</span></div>
                  <div className="text-sm text-ink-700 mt-1">
                    {it.verification.profession} · License <strong>{it.verification.license_number}</strong> ({it.verification.license_state})
                    {it.verification.license_expiration && <> · expires {it.verification.license_expiration}</>}
                  </div>
                  <div className="text-xs text-ink-500 mt-1">
                    Status: <strong>{it.verification.status}</strong>
                    {it.verification.submitted_at && <> · submitted {new Date(it.verification.submitted_at).toLocaleDateString()}</>}
                  </div>
                  {it.verification.reviewer_notes && (
                    <div className="text-xs text-ink-500 mt-1 italic">"{it.verification.reviewer_notes}"</div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => decide(it.user_id, "approve")} data-testid={`verify-approve-${it.user_id}`} className="min-h-[36px] px-3 rounded-full bg-sage-600 text-white text-xs inline-flex items-center gap-1 hover:bg-sage-700">
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button onClick={() => decide(it.user_id, "request_documents")} data-testid={`verify-request-${it.user_id}`} className="min-h-[36px] px-3 rounded-full border border-paper-300 text-xs inline-flex items-center gap-1 hover:bg-paper-100">
                    <FileQuestion className="w-3.5 h-3.5" /> Request docs
                  </button>
                  <button onClick={() => decide(it.user_id, "deny")} data-testid={`verify-deny-${it.user_id}`} className="min-h-[36px] px-3 rounded-full bg-terracotta-500 text-white text-xs inline-flex items-center gap-1 hover:bg-terracotta-600">
                    <X className="w-3.5 h-3.5" /> Deny
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================
function EarlyAdoptersTab() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showGrant, setShowGrant] = React.useState(false);
  const [audit, setAudit] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        api.get("/admin/billing/early-adopters"),
        api.get("/admin/billing/early-adopters/audit").catch(() => ({ data: null })),
      ]);
      setItems(r.data);
      setAudit(a.data);
    } catch (e) {
      toast.error("Could not load early adopters.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const revoke = async (userId) => {
    if (!window.confirm("Revoke Early Adopter status?")) return;
    try {
      await api.post(`/admin/billing/early-adopters/${userId}/revoke`);
      toast.success("Revoked.");
      load();
    } catch (e) {
      toast.error("Revoke failed.");
    }
  };

  return (
    <div data-testid="early-adopters-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="label-overline">Current early adopters ({items.length})</div>
        <button onClick={() => setShowGrant(true)} data-testid="grant-early-adopter-btn" className="min-h-[40px] px-4 rounded-full bg-ink-900 text-white text-sm hover:bg-ink-800 inline-flex items-center gap-2">
          <Crown className="w-3.5 h-3.5" /> Grant status
        </button>
      </div>

      {audit && audit.totals?.total > 0 && (
        <div data-testid="ea-audit-panel" className={`rounded-3xl border p-4 mb-4 ${
          audit.totals.mismatch > 0 ? "border-terracotta-500/40 bg-terracotta-500/5"
          : (audit.totals.odd_tier > 0 || audit.totals.inactive > 0) ? "border-amber-500/40 bg-amber-500/5"
          : "border-sage-500/40 bg-sage-500/5"}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm font-medium text-ink-900" data-testid="ea-audit-summary">
              Entitlement audit — {audit.totals.unlimited}/{audit.totals.total} unlimited
              {audit.totals.mismatch > 0
                ? <span className="text-terracotta-700 font-semibold"> · {audit.totals.mismatch} entitlement mismatch(es) — needs attention</span>
                : <span className="text-sage-700"> · no entitlement leaks ✓</span>}
              {audit.totals.odd_tier > 0 && <span className="text-amber-700"> · {audit.totals.odd_tier} non-standard tier</span>}
              {audit.totals.inactive > 0 && <span className="text-ink-500"> · {audit.totals.inactive} inactive/expired</span>}
            </div>
          </div>
          {audit.grants.filter((g) => g.flags.length > 0).length > 0 && (
            <ul className="mt-2.5 space-y-1" data-testid="ea-audit-flagged">
              {audit.grants.filter((g) => g.flags.length > 0).map((g) => (
                <li key={g.user_id} className="text-xs text-ink-700 flex flex-wrap items-center gap-x-2" data-testid={`ea-audit-row-${g.user_id}`}>
                  <span className="font-medium">{g.email}</span>
                  <span className="text-ink-500">grant {g.grant_type || "—"} · stored {g.ea_stored_tier || "—"} → effective {g.effective_tier}</span>
                  {g.flags.map((f) => (
                    <span key={f} className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                      f === "mismatch" ? "bg-terracotta-500/15 text-terracotta-700" : "bg-amber-500/15 text-amber-700"}`}>{f}</span>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {loading ? (
        <div className="text-ink-500 animate-pulse">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-ink-500 text-sm">No early adopters yet.</div>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li key={it.user_id} data-testid={`ea-row-${it.user_id}`} className="rounded-3xl border border-paper-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-ink-900">{it.name} · <span className="text-ink-600 font-normal">{it.email}</span></div>
                  <div className="text-sm text-ink-700 mt-1">
                    <strong>{it.early_adopter.grant_type}</strong> · tier <strong>{it.early_adopter.tier}</strong>
                    {it.early_adopter.expires_at ? <> · expires {new Date(it.early_adopter.expires_at).toLocaleDateString()}</> : <> · permanent</>}
                    {it.early_adopter.is_beta_tester && <span className="ml-2 px-2 py-0.5 rounded-full bg-sage-500/10 text-sage-700 text-[10px] font-medium">BETA</span>}
                  </div>
                  {it.early_adopter.reason && <div className="text-xs text-ink-500 mt-1 italic">"{it.early_adopter.reason}"</div>}
                </div>
                <button onClick={() => revoke(it.user_id)} data-testid={`ea-revoke-${it.user_id}`} className="min-h-[36px] px-3 rounded-full border border-paper-300 text-xs hover:bg-paper-100">
                  Revoke
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {showGrant && <GrantModal onClose={() => setShowGrant(false)} onDone={load} />}
    </div>
  );
}

function GrantModal({ onClose, onDone }) {
  const [email, setEmail] = React.useState("");
  const [form, setForm] = React.useState({
    grant_type: "lifetime",
    tier: "pro",
    reason: "",
    expires_at: "",
    notes: "",
    is_beta_tester: true,
  });
  const [busy, setBusy] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !form.reason) {
      toast.error("Email and reason are required.");
      return;
    }
    setBusy(true);
    try {
      // Resolve user_id by email
      let match;
      try {
        const r = await api.get("/admin/billing/users/search", { params: { email } });
        match = r.data;
      } catch (e) {
        if (e?.response?.status === 404) {
          toast.error("No user with that email. They must sign up first.");
          return;
        }
        throw e;
      }
      const payload = { ...form };
      if (!payload.expires_at) delete payload.expires_at;
      await api.post(`/admin/billing/early-adopters/${match.id}/grant`, payload);
      toast.success(`Granted Early Adopter to ${email}.`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="grant-modal" className="fixed inset-0 z-50 bg-ink-900/40 flex items-end md:items-center justify-center p-0 md:p-4">
      <form onSubmit={submit} className="bg-white w-full md:max-w-md md:rounded-3xl rounded-t-3xl p-6 space-y-3 max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="sticky -top-6 -mx-6 px-6 -mt-6 pt-6 pb-3 bg-white border-b border-paper-100 flex items-center justify-between mb-1">
          <h2 className="font-heading text-xl text-ink-900">Grant Early Adopter</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-paper-100"><X className="w-4 h-4" /></button>
        </div>
        <label className="block text-sm">
          <div className="label-overline mb-1.5">User email</div>
          <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="grant-email" placeholder="therapist@example.com" className="w-full min-h-[44px] px-3 rounded-2xl border border-paper-200" />
        </label>
        <label className="block text-sm">
          <div className="label-overline mb-1.5">Grant type</div>
          <select value={form.grant_type} onChange={(e) => setForm({ ...form, grant_type: e.target.value })} data-testid="grant-type" className="w-full min-h-[44px] px-3 rounded-2xl border border-paper-200 bg-white">
            <option value="lifetime">Lifetime</option>
            <option value="discounted">Discounted</option>
            <option value="free_year">Free year</option>
            <option value="free_quarter">Free quarter</option>
            <option value="beta">Beta tester only</option>
          </select>
        </label>
        <label className="block text-sm">
          <div className="label-overline mb-1.5">Tier granted</div>
          <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="grant-tier" className="w-full min-h-[44px] px-3 rounded-2xl border border-paper-200 bg-white">
            <option value="pro">Professional</option>
            <option value="clinic">Clinic</option>
          </select>
        </label>
        <label className="block text-sm">
          <div className="label-overline mb-1.5">Reason</div>
          <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="grant-reason" placeholder="Founder cohort, beta partner, feedback contributor…" className="w-full min-h-[44px] px-3 rounded-2xl border border-paper-200" />
        </label>
        <label className="block text-sm">
          <div className="label-overline mb-1.5">Expires (optional, blank = permanent)</div>
          <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="grant-expires" className="w-full min-h-[44px] px-3 rounded-2xl border border-paper-200" />
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_beta_tester} onChange={(e) => setForm({ ...form, is_beta_tester: e.target.checked })} data-testid="grant-beta" className="w-4 h-4 accent-sage-600" />
          <span>Beta tester (track for future feedback / validation)</span>
        </label>
        <label className="block text-sm">
          <div className="label-overline mb-1.5">Internal notes</div>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="grant-notes" rows={3} className="w-full px-3 py-2 rounded-2xl border border-paper-200" />
        </label>
        <button type="submit" disabled={busy} data-testid="grant-submit" className="w-full min-h-[44px] rounded-full bg-ink-900 text-white font-medium hover:bg-ink-800">
          {busy ? "Granting…" : "Grant Early Adopter"}
        </button>
      </form>
    </div>
  );
}

// ============================================================
function ConfigTab() {
  const [cfg, setCfg] = React.useState(null);
  const [editing, setEditing] = React.useState(null); // local editable copy

  const load = React.useCallback(async () => {
    const r = await api.get("/admin/billing/config");
    setCfg(r.data);
    setEditing(JSON.parse(JSON.stringify(r.data)));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  if (!cfg || !editing) return <div className="text-ink-500 animate-pulse">Loading…</div>;

  const save = async () => {
    try {
      await api.patch("/admin/billing/config", {
        tiers: editing.tiers,
        verification: editing.verification,
        providers: editing.providers,
        trial_days: editing.trial_days,
        currency: editing.currency,
      });
      toast.success("Saved.");
      load();
    } catch (e) {
      toast.error("Save failed.");
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset to factory defaults? Custom pricing will be lost.")) return;
    try {
      const r = await api.post("/admin/billing/config/reset");
      setCfg(r.data);
      setEditing(JSON.parse(JSON.stringify(r.data)));
      toast.success("Reset to defaults.");
    } catch (e) {
      toast.error("Reset failed.");
    }
  };

  const updateTier = (code, field, value) => {
    setEditing({
      ...editing,
      tiers: {
        ...editing.tiers,
        [code]: {
          ...editing.tiers[code],
          [field]: typeof editing.tiers[code][field] === "number" ? Number(value) : value,
        },
      },
    });
  };

  return (
    <div data-testid="config-tab" className="space-y-5">
      <div className="rounded-3xl border border-paper-200 bg-white p-5">
        <div className="label-overline mb-3">Tier pricing & quotas</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-500 text-xs uppercase tracking-wider">
                <th className="py-2 pr-3">Tier</th>
                <th className="py-2 pr-3">Label</th>
                <th className="py-2 pr-3">Monthly ($)</th>
                <th className="py-2 pr-3">Annual ($)</th>
                <th className="py-2 pr-3">AI quota / month</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(editing.tiers || {}).map(([code, t]) => (
                <tr key={code} className="border-t border-paper-200" data-testid={`config-row-${code}`}>
                  <td className="py-2 pr-3 font-mono text-xs">{code}</td>
                  <td className="py-2 pr-3">
                    <input value={t.label} onChange={(e) => updateTier(code, "label", e.target.value)} data-testid={`config-${code}-label`} className="min-h-[36px] px-2 rounded-lg border border-paper-200 w-full" />
                  </td>
                  <td className="py-2 pr-3">
                    <input type="number" value={(t.monthly_price_cents || 0) / 100} onChange={(e) => updateTier(code, "monthly_price_cents", Math.round((parseFloat(e.target.value) || 0) * 100))} data-testid={`config-${code}-monthly`} className="min-h-[36px] px-2 rounded-lg border border-paper-200 w-24" />
                  </td>
                  <td className="py-2 pr-3">
                    <input type="number" value={(t.annual_price_cents || 0) / 100} onChange={(e) => updateTier(code, "annual_price_cents", Math.round((parseFloat(e.target.value) || 0) * 100))} data-testid={`config-${code}-annual`} className="min-h-[36px] px-2 rounded-lg border border-paper-200 w-24" />
                  </td>
                  <td className="py-2 pr-3">
                    <input type="number" value={t.ai_quota_per_month || 0} onChange={(e) => updateTier(code, "ai_quota_per_month", parseInt(e.target.value) || 0)} data-testid={`config-${code}-quota`} className="min-h-[36px] px-2 rounded-lg border border-paper-200 w-20" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-ink-500 mt-3">Set AI quota to <strong>0</strong> for unlimited.</p>
      </div>

      <div className="rounded-3xl border border-paper-200 bg-white p-5">
        <div className="label-overline mb-3">Verification policy</div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!editing.verification?.required_for_paid} onChange={(e) => setEditing({ ...editing, verification: { ...editing.verification, required_for_paid: e.target.checked } })} data-testid="config-verify-required" className="w-4 h-4 accent-sage-600" />
          <span>Require professional verification before paid checkout</span>
        </label>
      </div>

      <div className="flex gap-2">
        <button onClick={save} data-testid="config-save-btn" className="min-h-[44px] px-5 rounded-full bg-ink-900 text-white font-medium hover:bg-ink-800">
          Save changes
        </button>
        <button onClick={reset} data-testid="config-reset-btn" className="min-h-[44px] px-5 rounded-full border border-paper-200 text-ink-700 hover:bg-paper-100">
          Reset to defaults
        </button>
      </div>
    </div>
  );
}
